import React, { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import Button from '../../../common/button';
import ModalWrap from '../../../common/modal-wrap';
import { CloseCrossBtn } from '../../../icons/close-cross-btn';

import forest from './../../../../assets/images/dpdg/forest.jpg';

export interface IVideoList {
  id: number;
  sort: number | null;
  title: string;
  description: string | null;
  thumbnail: string;
  video: string;
}

export interface IVideoOptionsResponse {
  data: IVideoList[];
}

export interface IBackgroundImageList {
  id: number;
  sort: number | null;
  title: string;
  description: string | null;
  image: string;
}

export interface IBackgroundImagesResponse {
  data: IBackgroundImageList[];
}

export type BackgroundSelection =
  | { type: 'none'; color: string }
  | { type: 'video'; item: IVideoList }
  | { type: 'image'; item: IBackgroundImageList };

interface IBackgroundModalProps {
  isShow: boolean;
  onClose: () => void;
  onApply: (selection: BackgroundSelection) => void;
  initialType: 'none' | 'video' | 'image';
  initialColor: string;
  initialValue: string;
  backgroundOptions: IVideoOptionsResponse;
  backgroundImages: IBackgroundImagesResponse;
  isVideosLoading?: boolean;
  isImagesLoading?: boolean;
}

const DIRECTUS_DOMAIN =
  ((import.meta.env.VITE_REACT_APP_DIRECTUS_DOMAIN as string | undefined) ?? 'https://d-t1.zingizi.pro').replace(
    /\/$/,
    '',
  );

const makeAssetUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;

  return `${DIRECTUS_DOMAIN}/assets/${path}`;
};

const BackgroundSettingsModal: FC<IBackgroundModalProps> = ({
  isShow,
  onClose,
  onApply,
  initialType,
  initialColor,
  initialValue,
  backgroundOptions,
  backgroundImages,
  isVideosLoading,
  isImagesLoading,
}) => {
  const videoTabEnabled = false; // TODO(analytics-team): вернуть вкладку «Видео», когда появится готовый контент и UX подгрузки роликов

  const [activeTab, setActiveTab] = useState<'color' | 'video' | 'image'>('color');
  const [selectedColor, setSelectedColor] = useState<string>(initialColor || '#000000');
  const [selectedMedia, setSelectedMedia] = useState<
    { type: 'video'; item: IVideoList } | { type: 'image'; item: IBackgroundImageList } | null
  >(null);

  const videos = useMemo(() => backgroundOptions?.data ?? [], [backgroundOptions]);
  const images = useMemo(() => backgroundImages?.data ?? [], [backgroundImages]);
  const defaultImage = forest;

  useEffect(() => {
    if (!isShow) {
      return;
    }

    if (videoTabEnabled && initialType === 'video') {
      setActiveTab('video');
    } else if (initialType === 'image') {
      setActiveTab('image');
    } else {
      setActiveTab('color');
    }
    setSelectedColor(initialColor || '#000000');

    if (initialType === 'video') {
      const currentVideo = videos.find((video) => video.video === initialValue);
      setSelectedMedia(currentVideo ? { type: 'video', item: currentVideo } : null);
    } else if (initialType === 'image') {
      const currentImage = images.find((image) => image.image === initialValue);
      setSelectedMedia(currentImage ? { type: 'image', item: currentImage } : null);
    } else {
      setSelectedMedia(null);
    }
  }, [isShow, initialType, initialColor, initialValue, images, videoTabEnabled, videos]);

  const handleApply = () => {
    if (activeTab === 'color') {
      onApply({ type: 'none', color: selectedColor });
      return;
    }

    if (activeTab === 'video' && selectedMedia?.type === 'video') {
      onApply({ type: 'video', item: selectedMedia.item });
      return;
    }

    if (activeTab === 'image' && selectedMedia?.type === 'image') {
      onApply({ type: 'image', item: selectedMedia.item });
    }
  };

  const renderColorPicker = () => (
    <div className="flex flex-col items-center gap-4 py-6">
      <div className="relative flex h-32 w-40 items-center justify-center">
        <span
          className="block h-24 w-36 rounded-[20px] border border-grey2 shadow-[inset_0_2px_8px_rgba(0,0,0,0.12)]"
          style={{ backgroundColor: selectedColor }}
        />
        <input
          aria-label="Выберите цвет фона"
          type="color"
          value={selectedColor}
          onChange={(event) => setSelectedColor(event.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </div>
      <div className="font-comfortaa text-xs uppercase tracking-[0.08em] text-greyBase">
        {selectedColor.toUpperCase()}
      </div>
    </div>
  );

  const renderVideos = () => {
    if (isVideosLoading) {
      return <div className="py-8 text-center text-xs text-greyBase">Загружаем список видео…</div>;
    }

    if (!videos.length) {
      return <div className="py-8 text-center text-xs text-greyBase">Нет доступных видео.</div>;
    }

    return (
      <div className="grid max-h-[45vh] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
        {videos.map((video) => {
          const selected = selectedMedia?.type === 'video' && selectedMedia.item.id === video.id;

          return (
            <button
              type="button"
              key={video.id}
              onClick={() => setSelectedMedia({ type: 'video', item: video })}
              className={classNames(
                'flex w-full items-start gap-3 rounded-[18px] bg-grey1/40 p-3 text-left transition-colors hover:bg-grey2',
                selected && '!bg-redBase !text-white hover:!bg-redBase',
              )}
            >
              <img
                src={makeAssetUrl(video.thumbnail)}
                alt={video.title}
                className="h-20 w-20 flex-shrink-0 rounded-[14px] object-cover"
                onError={(event) => {
                  event.currentTarget.src = defaultImage;
                }}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span
                  className={classNames(
                    'font-comfortaa text-sm font-semibold text-blackBase',
                    selected && 'text-white',
                  )}
                >
                  {video.title}
                </span>
                <span className={classNames('text-xs line-clamp-2', selected ? 'text-white/80' : 'text-greyBase')}>
                  {video.description?.trim() || 'Без описания'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderImages = () => {
    if (isImagesLoading) {
      return <div className="py-8 text-center text-xs text-greyBase">Загружаем изображения…</div>;
    }

    if (!images.length) {
      return <div className="py-8 text-center text-xs text-greyBase">Нет доступных изображений.</div>;
    }

    return (
      <div className="grid max-h-[45vh] grid-cols-1 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-2">
        {images.map((image) => {
          const selected = selectedMedia?.type === 'image' && selectedMedia.item.id === image.id;

          return (
            <button
              type="button"
              key={image.id}
              onClick={() => setSelectedMedia({ type: 'image', item: image })}
              className={classNames(
                'flex w-full items-start gap-3 rounded-[18px] bg-grey1/40 p-3 text-left transition-colors hover:bg-grey2',
                selected && '!bg-redBase !text-white hover:!bg-redBase',
              )}
            >
              <img
                src={makeAssetUrl(image.image)}
                alt={image.title}
                className="h-20 w-20 flex-shrink-0 rounded-[14px] object-cover"
                onError={(event) => {
                  event.currentTarget.src = defaultImage;
                }}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span
                  className={classNames(
                    'font-comfortaa text-sm font-bold text-blackBase line-clamp-1',
                    selected && 'text-white',
                  )}
                >
                  {image.title}
                </span>
                <span
                  className={classNames(
                    'text-xs line-clamp-2',
                    selected ? 'text-white/80' : 'text-greyBase',
                  )}
                >
                  {image.description?.trim() || 'Без описания'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const allTabs: Array<{ key: 'color' | 'video' | 'image'; label: string }> = [
    { key: 'color', label: 'Цвет' },
    // TODO(analytics-team): вернуть вкладку «Видео», когда готов контент и UX подгрузки роликов
    { key: 'video', label: 'Видео' },
    { key: 'image', label: 'Фото' },
  ];

  const tabs = videoTabEnabled ? allTabs : allTabs.filter((tab) => tab.key !== 'video');

  const isApplyDisabled =
    (videoTabEnabled && activeTab === 'video' && selectedMedia?.type !== 'video') ||
    (activeTab === 'image' && selectedMedia?.type !== 'image');

  return (
    <ModalWrap isShow={isShow}>
      <div className="relative flex w-[min(720px,92vw)] flex-col gap-5 rounded-[28px] bg-white p-6 shadow-shadowPopup">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#DEC05E] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-white/80"
          aria-label="Закрыть модальное окно"
        >
          <CloseCrossBtn width={18} height={18} fill="#DEC05E" fillBg="#DEC05E" />
        </button>

        <div className="pr-10">
          <h2 className="font-comfortaa text-lg font-bold text-blackBase lg:text-xl">Выберите фон</h2>
          <p className="mt-1.5 text-xs text-greyBase">
            Настройте фон демонстрации: можно выбрать однотонный цвет, видео или статичное изображение.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              // className="max-w-[160px] !text-base"
              type={activeTab === tab.key ? 'black' : 'grey'}
              className={classNames(
                '!w-auto !text-base !leading-none transition-colors',
                activeTab === tab.key
                  ? '!bg-blackBase !text-white shadow-[0_4px_12px_rgba(17,16,38,0.18)]'
                  : '!bg-grey2 !text-blackBase hover:!bg-grey3',
              )}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="min-h-[200px]">
          {activeTab === 'color' && renderColorPicker()}
          {videoTabEnabled && activeTab === 'video' && renderVideos()}
          {activeTab === 'image' && renderImages()}
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button type="grey" className="max-w-[160px] !text-base" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="max-w-[160px] !text-base"
            type="primary"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            Ок
          </Button>
        </div>
      </div>
    </ModalWrap>
  );
};

export default BackgroundSettingsModal;
